import { createSelector } from 'reselect';
import _get from 'lodash/get';
import getActivePaln from '../helpers/billing/getActivePlan';
import { isCardExpired } from '../helpers/billing/cardExpiry';
import isLatestSubscriptionPaymentFailedDueToExpiredCard from '../helpers/billing/isLatestSubscriptionPaymentFailedDueToExpiredCard';
import getTotalFeaturesAmount from '../../components/Billing/Overview/helpers/getTotalFeaturesAmount';

const userSelector = (state) => state.user;

export const billingPlansSelector = (state) => _get(state, 'billing.data.plans', []);

const subscriptionItemsSelector = (state) => _get(state, 'user.customer.subscription.items.data', []);

export const getSelectedPlan = (state) => state.billing.data.plans.find((plan) => plan.selectedPlan);

const selectAddonsFeatures = createSelector(userSelector, (user) => user.addonsFeatures || {});

export const getActivePlansSelector = createSelector(subscriptionItemsSelector, billingPlansSelector, getActivePaln);

/**
 * Returns the team-level `retentionCoupon` document set by the offboarding
 * flow ({ code, stripePromotionCodeId, generatedAt, appliedAt, redeemedAt })
 * or `null` when the user has never generated a retention coupon.
 */
export const getRetentionCoupon = (state) => _get(state, 'user.team.retentionCoupon', null);

export const getUser = createSelector(userSelector, (user) => {
  const {
    trialEnds,
    email,
    picsioStorage,
    subscriptionFeatures: {
      teammatesCount = 0,
      teammatesCountIncludingPending = 0,
      websitesCount = 0,
      teammatesLimit = 0,
      websitesLimit = 0,
      totalSize = 0,
    },
    customer: {
      balance, discount, card = {}, subscription = {},
    },
    team,
  } = user;
  const { coupon } = discount || {};
  const {
    current_period_end: currentPeriodEnd,
    cancel_at_period_end: cancelPeriodEnd,
  } = subscription || {};

  return {
    trialEnds,
    email,
    picsioStorage,
    card,
    coupon,
    // uncomment for coupon testing
    // coupon: {
    //   id: 'PICSIO_STORAGE_50POFF_ONEYEAR', object: 'coupon', amount_off: null, created: 1615891753, currency: null, duration: 'repeating', duration_in_months: 12, livemode: false, max_redemptions: null, metadata: {}, name: 'PICSIO_STORAGE_50POFF_ONEYEAR', percent_off: 50, redeem_by: null, times_redeemed: 10, valid: true,
    // },
    teammatesCount,
    teammatesCountIncludingPending,
    websitesCount,
    teammatesLimit,
    websitesLimit,
    balance,
    totalSize,
    subscription: {
      currentPeriodEnd,
      cancelPeriodEnd,
      subscriptionItems: subscription?.items?.data || [],
    },
    team,
  };
});

export const selectShouldShowExpiredCardPaymentWarning = createSelector(
  (state) => state.billing?.invoices,
  (state) => _get(state, 'user.customer.card', null),
  (invoices, card) => isCardExpired(card)
    && isLatestSubscriptionPaymentFailedDueToExpiredCard(invoices),
);

export const selectTotalAddons = createSelector(
  selectAddonsFeatures,
  getSelectedPlan,
  (addonsFeatures, selectedPlan) => {
    const planToUpdate = _get(selectedPlan, 'planToUpdate', {});

    const { addons } = getTotalFeaturesAmount(
      planToUpdate.addons || [],
      planToUpdate.metadata || {},
      addonsFeatures,
    );

    return { addons };
  },
);

export default {
  getActivePlansSelector,
  getUser,
};
